/**
 * Decode pixel dimensions from a local image blob (browser `Image` + object URL).
 */
import { MAX_IMAGE_UPLOAD_SIZE_BYTES } from '~/constants/file'

/**
 * Decode pixel dimensions from a local image blob (browser `Image` + object URL).
 *
 * @param blob The blob to decode the image dimensions from.
 * @returns The decoded image dimensions.
 */
export function getDecodedImageDimensionsFromBlob(blob: Blob): Promise<{ width: number, height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image'))
    }
    img.src = url
  })
}

/**
 * Pintura `imageCropMinSize` for free listing crops (width × height in source pixels).
 * Both edges use the listing short-side floor so any aspect is allowed without tiny slices.
 *
 * @param minShortSide The minimum short side of a listing photo
 * @returns The image crop min size
 */
export function getFreeCropImageMinSize(
  minShortSide: number,
): { width: number, height: number } {
  return { width: minShortSide, height: minShortSide }
}

/**
 * True when decoded pixel width/height meet minimum long and short edge (any orientation).
 *
 * @param width The width of the image
 * @param height The height of the image
 * @param minLongSide The minimum long side
 * @param minShortSide The minimum short side
 * @returns True if the image meets the minimum dimensions, false otherwise
 */
export function meetsMinimumImageDimensions(
  width: number,
  height: number,
  minLongSide: number,
  minShortSide: number,
): boolean {
  const longSide = Math.max(width, height)
  const shortSide = Math.min(width, height)

  return longSide >= minLongSide && shortSide >= minShortSide
}

/**
 * True when a local image file exceeds the maximum allowed upload size.
 *
 * @param sizeInBytes The file size in bytes.
 * @returns True when the file exceeds the maximum allowed upload size.
 */
export function exceedsMaxImageUploadSize(sizeInBytes: number): boolean {
  return sizeInBytes > MAX_IMAGE_UPLOAD_SIZE_BYTES
}

/**
 * True when processed output is at least as large as the source on both long and short edges.
 *
 * @param outputWidth The width of the output.
 * @param outputHeight The height of the output.
 * @param sourceWidth The width of the source.
 * @param sourceHeight The height of the source.
 * @returns True when processed output is at least as large as the source on both long and short edges.
 */
export function processedImagePreservesSourceResolution(
  outputWidth: number,
  outputHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): boolean {
  const outputLong = Math.max(outputWidth, outputHeight)
  const outputShort = Math.min(outputWidth, outputHeight)
  const sourceLong = Math.max(sourceWidth, sourceHeight)
  const sourceShort = Math.min(sourceWidth, sourceHeight)

  return outputLong >= sourceLong && outputShort >= sourceShort
}
